import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { deleteAudit, unwrapPost } from "../../../../helpers/auditHub_helper";
import { AUDIT_STATUSES } from "../constants";
import { AH_ACCENT, AH_TEXT, AH_WHITE, auditHubPageSx } from "../theme";

const DELETE_ERROR_MESSAGES = {
  NOT_AUDIT_OWNER: "Only the assessment owner can delete a completed or cancelled assessment.",
  AUDIT_NOT_DELETABLE: "An in-progress assessment cannot be deleted. Complete or cancel it first.",
  AUDIT_SCAN_IN_FLIGHT: "A scan is still running for this assessment. Try again once it finishes.",
  AUDIT_REPORT_IN_FLIGHT: "A report is still being generated for this assessment. Try again once it finishes.",
};

const deleteErrorMessage = (err) =>
  DELETE_ERROR_MESSAGES[err?.data?.error?.code] || err?.message || "Unable to delete this assessment.";

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const DeleteAuditDialog = ({ audit, open, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open, audit?.auditId]);

  const confirm = async () => {
    if (!audit?.auditId || deleting) return;
    setDeleting(true);
    setError("");
    try {
      unwrapPost(await deleteAudit(audit.auditId));
      onDeleted(audit);
    } catch (err) {
      setError(deleteErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const label = [audit?.auditCode, audit?.name].filter(Boolean).join(" · ");
  const isDraft = audit?.status === AUDIT_STATUSES.DRAFT;

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          minHeight: 56,
          px: 3,
          backgroundColor: AH_ACCENT,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
          Delete Assessment
        </Typography>
        <Box
          component="button"
          type="button"
          aria-label="Close"
          disabled={deleting}
          onClick={onClose}
          sx={{
            p: 0,
            m: 0,
            border: "none",
            background: "transparent",
            color: AH_WHITE,
            fontSize: 28,
            fontWeight: 700,
            lineHeight: 1,
            cursor: deleting ? "default" : "pointer",
          }}
        >
          ×
        </Box>
      </Box>
      <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
        <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6 }}>
          Are you sure you want to permanently delete{" "}
          {label ? (
            <Box component="span" sx={{ fontWeight: 600 }}>
              {label}
            </Box>
          ) : (
            "this assessment"
          )}
          ?
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
          {isDraft
            ? "This action cannot be undone."
            : "The assessment and all of its data will be permanently removed. This action cannot be undone."}
        </Typography>
      </DialogContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#f8fafc",
        }}
      >
        <Button
          onClick={onClose}
          disabled={deleting}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#74788d",
            color: "#eff2f7",
            "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
          }}
        >
          Cancel
        </Button>
        {error ? (
          <Typography
            role="alert"
            sx={{
              flex: 1,
              minWidth: 0,
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: "100%", sm: "auto" },
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "#dc2626",
              textAlign: "center",
              overflowWrap: "anywhere",
            }}
          >
            {error}
          </Typography>
        ) : null}
        <Button
          onClick={confirm}
          disabled={deleting || !audit?.auditId}
          sx={{
            ...dialogButtonSx,
            backgroundColor: "#dc2626",
            color: AH_WHITE,
            "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#dc2626", color: AH_WHITE, opacity: 0.65 },
          }}
        >
          {deleting ? "Deleting" : "Delete"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteAuditDialog;
